import { useState, useEffect, useMemo } from "react";

import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import apiService from "../services/api";

const ResellerTierJourney = ({ totalUsers = 0 }) => {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  const TIER_UI_CONFIG = {
    nano: { color: "#8B5CF6", icon: "fa-seedling" },
    starter: { color: "#3B82F6", icon: "fa-rocket" },
    basic: { color: "#06B6D4", icon: "fa-star" },
    plus: { color: "#10B981", icon: "fa-gem" },
    growth: { color: "#F59E0B", icon: "fa-fire" },
    pro: { color: "#F97316", icon: "fa-crown" },
    scale: { color: "#EF4444", icon: "fa-trophy" },
    "enterprise-1": { color: "#EC4899", icon: "fa-building" },
    "enterprise-2": { color: "#D946EF", icon: "fa-bullseye" },
    "enterprise-3": { color: "#8B5CF6", icon: "fa-bolt" },
    "enterprise-4": { color: "#6366F1", icon: "fa-layer-group" },
  };

  useEffect(() => {
    const fetchTiers = async () => {
      setLoading(true);
      try {
        const response = await apiService.getResellerTiers();
        const tiersData = response.data?.tiers || [];
        const sortedTiers = tiersData.sort((a, b) => a.tierOrder - b.tierOrder);
        const processedTiers = sortedTiers.map((tier, index) => {
          const minUsers =
            index === 0 ? 25 : sortedTiers[index - 1].maxUsers + 1;
          const uiConfig = TIER_UI_CONFIG[tier.tierSlug] || {
            color: "#6B7280",
            icon: "fa-circle",
          };

          return {
            name: tier.tierName,
            slug: tier.tierSlug,
            minUsers,
            maxUsers: tier.maxUsers,
            order: tier.tierOrder,
            discount: tier.discountPercentage,
            ...uiConfig,
          };
        });

        setTiers(processedTiers);
      } catch (error) {
        console.error("Error fetching tiers:", error);
        setTiers([
          {
            name: "Nano",
            slug: "nano",
            minUsers: 25,
            maxUsers: 49,
            order: 1,
            discount: 10,
            ...TIER_UI_CONFIG.nano,
          },
          {
            name: "Starter",
            slug: "starter",
            minUsers: 50,
            maxUsers: 99,
            order: 2,
            discount: 15,
            ...TIER_UI_CONFIG.starter,
          },
          {
            name: "Basic",
            slug: "basic",
            minUsers: 100,
            maxUsers: 199,
            order: 3,
            discount: 20,
            ...TIER_UI_CONFIG.basic,
          },
          {
            name: "Plus",
            slug: "plus",
            minUsers: 200,
            maxUsers: 299,
            order: 4,
            discount: 25,
            ...TIER_UI_CONFIG.plus,
          },
          {
            name: "Growth",
            slug: "growth",
            minUsers: 300,
            maxUsers: 499,
            order: 5,
            discount: 30,
            ...TIER_UI_CONFIG.growth,
          },
          {
            name: "Pro",
            slug: "pro",
            minUsers: 500,
            maxUsers: 999,
            order: 6,
            discount: 35,
            ...TIER_UI_CONFIG.pro,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchTiers();
  }, []);

  const { currentTier, progress, nextTier, usersToNext, isMaxTier } =
    useMemo(() => {
      const current = tiers.find(
        (t) => totalUsers >= t.minUsers && totalUsers <= t.maxUsers
      );
      const index = current
        ? tiers.findIndex((t) => t.slug === current.slug)
        : -1;
      const next = index >= 0 ? tiers[index + 1] : tiers[0];

      let prog = 0;

      if (current && next) {
        const range = next.minUsers - current.minUsers;
        const currentProgress = totalUsers - current.minUsers;

        prog = Math.min((currentProgress / range) * 100, 100);
      }

      return {
        currentTier: current,
        progress: prog,
        nextTier: next,
        usersToNext: next ? next.minUsers - totalUsers : 0,
        isMaxTier: current && !next,
      };
    }, [tiers, totalUsers]);

  if (loading) {
    return (
      <div className="mb-4 h-20 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
        <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Max tier reached
  if (isMaxTier) {
    return (
      <div className="mb-4">
        <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 dark:from-amber-500/20 dark:to-yellow-500/20 rounded-xl p-4 border border-amber-500/30">
          <div className="flex items-center justify-center gap-3">
            <span className="text-2xl">🏆</span>
            <div className="text-center">
              <div className="font-bold text-gray-900 dark:text-white">
                Maximum Tier Reached!
              </div>
              <div className="text-sm text-amber-600 dark:text-amber-400">
                {currentTier.name} • {currentTier.discount}% discount
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl py-4 px-1 sm:px-4 border border-gray-200 dark:border-gray-700">
        {/* Roadmap - Grid layout */}
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 sm:gap-4">
          {/* Current Tier Node */}
          <div
            className="flex flex-col items-center cursor-pointer group px-2.5"
            onClick={() => navigate("/reseller-tiers")}
          >
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-transform group-hover:scale-110"
              style={{
                backgroundColor: currentTier?.color || "#6B7280",
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <i
                className={`fas ${currentTier?.icon || "fa-flag"} text-white`}
              />
            </motion.div>
            <div className="mt-2 text-center">
              <div className="text-xs font-semibold text-gray-900 dark:text-white">
                {currentTier?.name || "Start"}
              </div>
              {currentTier && (
                <div className="text-[10px] text-gray-500 dark:text-gray-400">
                  {currentTier.discount}% off
                </div>
              )}
            </div>
          </div>

          {/* Progress Path */}
          <div className="flex flex-col items-center gap-2">
            {/* Progress Bar Container */}
            <div className="w-full relative pt-8 pb-1">
              {/* Progress Bar Track */}
              <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <motion.div
                  animate={{ width: `${progress}%` }}
                  className="h-full rounded-full"
                  initial={{ width: 0 }}
                  style={{
                    background: `linear-gradient(90deg, ${currentTier?.color || "#8B5CF6"}, ${nextTier?.color || "#8B5CF6"})`,
                  }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </div>
              {/* You are here marker - positioned outside overflow container */}
              <motion.div
                animate={{ left: `${progress}%` }}
                className="absolute -translate-y-1/2 -translate-x-1/2"
                initial={{ left: 0 }}
                style={{ top: "calc(2rem + 6px)" }}
                transition={{ duration: 1, ease: "easeOut" }}
              >
                <div className="relative">
                  <div
                    className="w-5 h-5 rounded-full border-3 border-white shadow-lg"
                    style={{
                      backgroundColor: currentTier?.color || "#8B5CF6",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    }}
                  />
                  {/* Tooltip Bubble */}
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <div className="bg-gray-900 dark:bg-gray-700 text-white text-[10px] px-3 py-1 rounded-full shadow-lg relative">
                      You're here
                      {/* Arrow */}
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45" />
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Progress Info */}
            <div className="flex items-center justify-between w-full text-xs text-gray-500 dark:text-gray-400">
              <span className="text-left">{currentTier?.minUsers || 0}</span>
              <span className="font-medium text-purple-600 dark:text-purple-400 text-center">
                +{usersToNext}
              </span>
              <span className="text-right">{nextTier?.minUsers}</span>
            </div>
          </div>

          {/* Next Tier Node */}
          {nextTier && (
            <div
              className="flex flex-col items-center cursor-pointer group px-2.5"
              onClick={() => navigate("/reseller-tiers")}
            >
              <motion.div
                animate={{
                  boxShadow: [
                    `0 0 0 0 ${nextTier.color}40`,
                    `0 0 0 8px ${nextTier.color}00`,
                  ],
                }}
                className="w-12 h-12 rounded-full flex items-center justify-center border-2 border-dashed transition-transform group-hover:scale-110"
                style={{ borderColor: nextTier.color }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <i
                  className={`fas ${nextTier.icon}`}
                  style={{ color: nextTier.color }}
                />
              </motion.div>
              <div className="mt-2 text-center">
                <div className="text-xs font-semibold text-gray-900 dark:text-white">
                  {nextTier.name}
                </div>
                <div className="text-[10px] text-green-600 dark:text-green-400 font-medium">
                  {nextTier.discount}% off
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResellerTierJourney;
